import styled from "styled-components";
import { Column, DatePicker, InputText, Row } from "@ficatranquilo/contabilidade.com-ui";
import { isoToDate, localIsoDate } from "@helpers/localDate";

// Campos e validação do projeto, comuns ao novo projeto (página) e ao editar (modal
// do detalhe). Os ids dos campos levam o prefixo `idPrefix` ("project" por padrão).

const ERP_MAX = 100;

// O `$centralize` do DatePicker centraliza o calendário no ancestral posicionado mais próximo.
// O `Column` da lib é `relative`: no modal, a coluna das datas fica `static` e o calendário
// centraliza pela linha inteira, que tem a largura do modal.
const StaticColumn = styled(Column)`
  position: static;
`;

/** Erros por id do campo, na ordem da tela. `values`: { name, erpCode, startDate, endDate }. */
export function validateProject({ name, startDate, endDate, erpCode }, idPrefix = "project") {
  const errors = {};
  if (!name.trim()) errors[`${idPrefix}-name`] = "Informe o nome do projeto.";
  if (erpCode.trim().length > ERP_MAX) errors[`${idPrefix}-erp`] = `Use até ${ERP_MAX} caracteres.`;
  if (!startDate) errors[`${idPrefix}-start`] = "Informe a data de início.";
  if (startDate && endDate && endDate < startDate) errors[`${idPrefix}-end`] = "O término não pode ser antes do início.";
  return errors;
}

/** Corpo que a API recebe no criar e no editar: datas em texto `YYYY-MM-DD`. */
export function projectPayload({ name, startDate, endDate, erpCode }) {
  return {
    name: name.trim(),
    start_date: startDate,
    end_date: endDate || null,
    erp_code: erpCode.trim() || null,
  };
}

/**
 * Nome, código ERP, início e término (o término pode ser limpo). `values` como em `validateProject`,
 * `onChange(campo, valor)` e `errors` por id do campo. Em modal, `inModal` abre o calendário no
 * centro dele, para a borda do modal não cortá-lo.
 */
export function ProjectFields({ values, onChange, errors = {}, idPrefix = "project", inModal = false }) {
  const id = (field) => `${idPrefix}-${field}`;
  const DateColumn = inModal ? StaticColumn : Column;

  return (
    <>
      <Row $gap={20}>
        <Column $columns={8}>
          <InputText
            $idForm={id("name")}
            $label="Nome do projeto"
            $variant="default"
            $isRequired
            $errors={errors[id("name")]}
            placeholder="Ex.: Portal do Cliente"
            value={values.name}
            onChange={(e) => onChange("name", e.target.value)}
          />
        </Column>
        <Column $columns={4}>
          <InputText
            $idForm={id("erp")}
            $label="Código ERP"
            $variant="default"
            $errors={errors[id("erp")]}
            maxLength={ERP_MAX}
            placeholder="Ex.: ERP-001"
            value={values.erpCode}
            onChange={(e) => onChange("erpCode", e.target.value)}
          />
        </Column>
      </Row>
      <Row $gap={20}>
        <DateColumn $columns={4}>
          <DatePicker
            $idForm={id("start")}
            $label="Início"
            $variant="default"
            $isRequired
            $hasTooltip="Os apontamentos de horas podem ser feitos a partir desta data."
            $errors={errors[id("start")]}
            $popperPlacement="bottom-start"
            $centralize={inModal}
            $date={isoToDate(values.startDate)}
            $setDate={(date) => onChange("startDate", localIsoDate(date) || "")}
          />
        </DateColumn>
        <DateColumn $columns={4}>
          <DatePicker
            $idForm={id("end")}
            $label="Término"
            $variant="default"
            $hasTooltip="Os apontamentos de horas podem ser feitos até esta data. Sem término, o projeto fica aberto."
            $errors={errors[id("end")]}
            $popperPlacement="bottom-start"
            $centralize={inModal}
            $isClearable
            $minDate={isoToDate(values.startDate) || undefined}
            $date={isoToDate(values.endDate)}
            $setDate={(date) => onChange("endDate", localIsoDate(date) || "")}
          />
        </DateColumn>
      </Row>
    </>
  );
}
